import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ManagePartnerAccountsProps } from './types';
import { styles } from './styles';
import { Modal, Button, Input, Spinner, Alert } from '@v2/components';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import {
  useAccountsWithPartnerDomains,
  useAddAccountToPartner,
  useRemoveAccountFromPartner,
} from '@v2/hooks';
import { AccountWithPartnerDomains } from '@v2/types';

const searchKeys: (keyof AccountWithPartnerDomains)[] = [
  'fullName',
  'firstName',
  'lastName',
  'email',
];

function matchesSearch(account: AccountWithPartnerDomains, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  return searchKeys.some((key) => {
    const value = account[key];
    return typeof value === 'string' && value.toLowerCase().includes(q);
  });
}

const ManagePartnerAccounts: React.FC<ManagePartnerAccountsProps> = ({
  onClose,
  onCompleted,
}) => {
  const location = useLocation();
  const partnerId = new URLSearchParams(location.search).get('partnerId');
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingRemoveAccountId, setPendingRemoveAccountId] = useState<string | null>(null);

  const { accounts, loading, error, refetch } =
    useAccountsWithPartnerDomains(partnerId);
  const { addAccountToPartner, loading: addLoading } =
    useAddAccountToPartner();
  const { removeAccountFromPartner, loading: removeLoading } =
    useRemoveAccountFromPartner();

  const actionLoading = addLoading || removeLoading;

  const handleAdd = async (accountId: string) => {
    if (!partnerId) return;
    const result = await addAccountToPartner(partnerId, accountId);
    if (result !== null) {
      toast.success('Account added to partner');
      await refetch();
      onCompleted?.();
    } else {
      toast.error('Failed to add account to partner');
    }
  };

  const handleConfirmRemove = async () => {
    if (!partnerId || !pendingRemoveAccountId) return;
    const accountId = pendingRemoveAccountId;
    const success = await removeAccountFromPartner(partnerId, accountId);
    setPendingRemoveAccountId(null);
    if (success) {
      toast.success('Account removed from partner');
      await refetch();
      onCompleted?.();
    } else {
      toast.error('Failed to remove account from partner');
    }
  };

  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return accounts.filter((account) => matchesSearch(account, searchQuery));
  }, [accounts, searchQuery]);

  const showResults = searchQuery.trim().length > 0;

  const handleManageClose = () => {
    if (pendingRemoveAccountId !== null) {
      setPendingRemoveAccountId(null);
      return;
    }
    onClose();
  };

  return (
    <>
    <Modal
      isOpen={true}
      onClose={handleManageClose}
      title="Manage Accounts"
      size="md"
      footer={
        <Button variant="primary" onClick={handleManageClose}>
          Close
        </Button>
      }
    >
      <div className={styles.searchContainer}>
        <Input
          label="Search accounts"
          placeholder="Type to search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className={styles.resultsContainer}>
        {loading ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8">
            <Spinner size="lg" className="tw-text-primary-600 tw-mb-3" />
            <span className="tw-text-neutral-500">Loading...</span>
          </div>
        ) : error ? (
          <Alert variant="error">{error}</Alert>
        ) : !showResults ? (
          <div className={styles.noSearchYet}>
            Start typing to see matching accounts.
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className={styles.emptyResults}>No matching accounts.</div>
        ) : (
          <ul className={styles.resultsList}>
            {filteredAccounts.map((account) => (
              <li key={account.id} className={styles.resultItem}>
                <div className={styles.resultItemContent}>
                  <div className={styles.resultItemName}>{account.fullName}</div>
                  <div className={styles.resultItemEmail}>{account.email}</div>
                </div>
                <div className={styles.resultItemCta}>
                  {account.connectedToPartner ? (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setPendingRemoveAccountId(account.id)}
                      disabled={actionLoading}
                    >
                      Remove
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleAdd(account.id)}
                      disabled={actionLoading}
                    >
                      Add
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>

    <DeleteConfirmationModal
      isOpen={pendingRemoveAccountId !== null}
      onClose={() => setPendingRemoveAccountId(null)}
      onConfirm={handleConfirmRemove}
      isDeleting={removeLoading}
    />
    </>
  );
};

export default ManagePartnerAccounts;
